import { redactSensitive } from './redactSensitive';

const PIPEDREAM_LOG_URL = 'https://in.logs.betterstack.com/';
const SF_LOG_URL = 'https://s2440157.eu-fsn-3.betterstackdata.com';
// A slow log must not hold up the request it describes.
const LOG_TIMEOUT_MS = 1500;

const DEPLOYMENT = {
  site: 'reservemyloan',
  environment:
    process.env.VERCEL_TARGET_ENV ||
    process.env.VERCEL_ENV ||
    process.env.NODE_ENV,
  branch: process.env.VERCEL_GIT_COMMIT_REF ?? null,
};

async function sendLog(
  payload: object,
  token: string,
  url = PIPEDREAM_LOG_URL
) {
  try {
    await fetch(url, {
      method: 'POST',
      signal: AbortSignal.timeout(LOG_TIMEOUT_MS),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        dt: new Date().toISOString(),
        service: 'pipedream',
        ...DEPLOYMENT,
        ...(redactSensitive(payload) as object),
      }),
    });
  } catch {
    // Never let logging failures affect the main request
  }
}

interface PipedreamWarningLog {
  url: string;
  minutes: number;
  submissionId?: string;
  prospectId?: string;
}

export async function logPipedreamWarning({
  url,
  minutes,
  submissionId,
  prospectId,
}: PipedreamWarningLog) {
  const token = process.env.BETTERSTACK_SOURCE_TOKEN;
  if (!token) return;

  await sendLog(
    {
      level: 'warn',
      message: `Pipedream request to ${url} is still pending after ${minutes} minute${minutes > 1 ? 's' : ''}`,
      url,
      pending_minutes: minutes,
      submission_id: submissionId ?? null,
      prospect_id: prospectId ?? null,
    },
    token
  );
}

interface PipedreamErrorLog {
  durationMs: number;
  errorMessage: string;
  statusCode?: number;
  url?: string;
  submissionId?: string;
  prospectId?: string;
  requestBody?: unknown;
  responseBody?: unknown;
}

interface SalesforceErrorLog {
  durationMs: number;
  errorMessage: string;
  statusCode?: number;
  url?: string;
  requestBody?: unknown;
  responseBody?: unknown;
  recordId?: string;
  pageUrl?: string;
}

export async function logSalesforceError(payload: SalesforceErrorLog) {
  const token = process.env.BETTERSTACK_SF_SOURCE_TOKEN;
  if (!token) return;

  await sendLog(
    {
      level: 'error',
      message: `Salesforce error on ${payload.url}: ${payload.errorMessage}`,
      duration_ms: payload.durationMs,
      status_code: payload.statusCode ?? null,
      url: payload.url ?? null,
      request_body: payload.requestBody ?? null,
      response_body: payload.responseBody ?? null,
      ...(payload.recordId ? { record_id: payload.recordId } : {}),
      ...(payload.pageUrl ? { page_url: payload.pageUrl } : {}),
      service: 'salesforce',
    },
    token,
    SF_LOG_URL
  );
}

interface ApiRejectionLog {
  method: string;
  url: string;
  reason: 'forbidden' | 'invalid-body' | 'rate-limited' | 'proof-required';
  enforced: boolean;
  page?: string | null;
  fields?: string[];
}

export async function logApiRejection(payload: ApiRejectionLog) {
  const token = process.env.BETTERSTACK_SF_SOURCE_TOKEN;
  if (!token) return;

  await sendLog(
    {
      level: 'warn',
      message: `API request ${payload.enforced ? 'refused' : 'would be refused'} (${payload.reason}) on ${payload.method} ${payload.url}`,
      method: payload.method,
      url: payload.url,
      reason: payload.reason,
      enforced: payload.enforced,
      page: payload.page ?? 'unknown',
      ...(payload.fields ? { fields: payload.fields } : {}),
      service: 'record-access',
    },
    token,
    SF_LOG_URL
  );
}

interface LastFourCheckLog {
  outcome:
    | 'match'
    | 'mismatch'
    | 'no-ssn'
    | 'invalid'
    | 'rate-limited'
    | 'forbidden';
  recordType: string;
  recordId: string;
  page: string | null;
  next: string | null;
  proof: 'grant' | 'legacy' | 'last-4' | null;
  limit?: 'ip' | 'record';
}

export async function logLastFourCheck(payload: LastFourCheckLog) {
  const token = process.env.BETTERSTACK_SF_SOURCE_TOKEN;
  if (!token) return;

  await sendLog(
    {
      level: payload.outcome === 'match' ? 'info' : 'warn',
      message: `last-4 ${payload.outcome} on ${payload.next ?? payload.page ?? 'unknown'}`,
      reason: 'last-4',
      outcome: payload.outcome,
      record_type: payload.recordType,
      record_id: payload.recordId,
      page: payload.page ?? 'unknown',
      next: payload.next,
      proof: payload.proof,
      ...(payload.limit ? { limit: payload.limit } : {}),
      service: 'record-access',
    },
    token,
    SF_LOG_URL
  );
}

interface LinkAccessLog {
  kind: 'legacy-link' | 'unproven-link' | 'legacy-record';
  path: string;
  recordType: string;
  recordId: string;
  enforced: boolean;
}

export async function logLinkAccess(payload: LinkAccessLog) {
  const token = process.env.BETTERSTACK_SF_SOURCE_TOKEN;
  if (!token) return;

  await sendLog(
    {
      level: payload.kind === 'unproven-link' ? 'warn' : 'info',
      message: `${payload.kind} on ${payload.path}`,
      reason: payload.kind,
      path: payload.path,
      record_type: payload.recordType,
      record_id: payload.recordId,
      enforced: payload.enforced,
      service: 'record-access',
    },
    token,
    SF_LOG_URL
  );
}

export async function logEmptyOffersWarning(submissionId: string) {
  const token = process.env.BETTERSTACK_SOURCE_TOKEN;
  if (!token) return;

  let pageUrl: string | undefined;
  try {
    const { headers } = await import('next/headers');
    const headersList = await headers();
    pageUrl = headersList.get('referer') ?? undefined;
  } catch {
    // not available outside a request context
  }

  await sendLog(
    {
      level: 'warn',
      message: `No offers returned for submission ${submissionId}`,
      submission_id: submissionId,
      ...(pageUrl ? { page_url: pageUrl } : {}),
    },
    token
  );
}

export async function logPipedreamError(payload: PipedreamErrorLog) {
  const token = process.env.BETTERSTACK_SOURCE_TOKEN;
  if (!token) return;
  const isTimeout =
    payload.errorMessage.includes('ECONNABORTED') ||
    payload.errorMessage.includes('ETIMEDOUT') ||
    payload.errorMessage.includes('ECONNRESET') ||
    payload.errorMessage.includes('ERR_CANCELED') ||
    payload.errorMessage.toLowerCase().includes('timeout');

  await sendLog(
    {
      level: 'error',
      message: isTimeout
        ? `Pipedream timeout on ${payload.url} after ${payload.durationMs}ms`
        : `Pipedream error on ${payload.url}: ${payload.errorMessage}`,
      duration_ms: payload.durationMs,
      status_code: payload.statusCode ?? null,
      url: payload.url ?? null,
      is_timeout: isTimeout,
      submission_id: payload.submissionId ?? null,
      prospect_id: payload.prospectId ?? null,
      request_body: payload.requestBody ?? null,
      response_body: payload.responseBody ?? null,
    },
    token
  );
}
